import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, FileCheck, ImagePlus, X } from 'lucide-react';
import type { ExportResult } from '../types';

interface ExportResultModalProps {
  visible: boolean;
  result: ExportResult | null;
  onClose: () => void;
}

/**
 * Modal minimalista para mostrar resultado da exportação
 * Segue o padrão UI/UX do projeto
 */
export function ExportResultModal({ visible, result, onClose }: ExportResultModalProps) {
  if (!visible || !result) return null;

  // Determinar tipo de resultado
  const isPartialSuccess = result.partialSuccess === true;
  const isSuccess = result.success && !isPartialSuccess;
  const isFailure = !result.success && !isPartialSuccess;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-navy-900/90 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] bg-navy-800 rounded-lg shadow-xl z-50 border border-navy-700/50 max-h-[80vh] flex flex-col">
        {/* Header com status */}
        <div className="p-3 border-b border-navy-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isSuccess && <CheckCircle2 className="w-4 h-4 text-green-400" />}
            {isPartialSuccess && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
            {isFailure && <XCircle className="w-4 h-4 text-red-400" />}
            <h3 className="text-sm font-semibold text-white">
              {isSuccess ? 'Exportação Completa' : isPartialSuccess ? 'Exportação Parcial' : 'Exportação Falhou'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-navy-700/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-3 space-y-2 overflow-auto flex-1 min-h-0">
          {/* Produção */}
          {result.production && (
            <div
              className={`p-2.5 rounded border ${
                result.production.success
                  ? 'bg-green-400/10 border-green-400/30'
                  : 'bg-red-400/10 border-red-400/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <FileCheck className={`w-3.5 h-3.5 ${result.production.success ? 'text-green-400' : 'text-red-400'}`} />
                <div className="text-xs font-medium text-white">
                  Produção {result.production.success ? '✓' : '✗'}
                </div>
              </div>

              {result.production.success ? (
                <div className="space-y-0.5 text-[11px] text-gray-400">
                  <div>📦 {result.production.files} arquivo(s)</div>
                  <div className="truncate" title={result.production.outputDir}>
                    📂 ...{result.production.outputDir.slice(-40)}
                  </div>
                  {result.production.registered && (
                    <div className="text-green-400">✓ Registrado</div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-red-400 leading-snug">
                  {result.production.error || 'Erro desconhecido'}
                </div>
              )}
            </div>
          )}

          {/* Layout */}
          {result.layout && (
            <div
              className={`p-2.5 rounded border ${
                result.layout.success
                  ? 'bg-green-400/10 border-green-400/30'
                  : 'bg-red-400/10 border-red-400/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <ImagePlus className={`w-3.5 h-3.5 ${result.layout.success ? 'text-green-400' : 'text-red-400'}`} />
                <div className="text-xs font-medium text-white">
                  Layout PNG {result.layout.success ? '✓' : '✗'}
                </div>
              </div>

              {result.layout.success ? (
                <div className="space-y-0.5 text-[11px] text-gray-400">
                  <div>🖼️ {result.layout.files} PNG(s)</div>
                  <div className="truncate" title={result.layout.outputDir}>
                    📂 ...{result.layout.outputDir.slice(-40)}
                  </div>
                  {result.layout.registered && (
                    <div className="text-green-400">✓ Registrado</div>
                  )}
                  {result.layout.uploaded > 0 && (
                    <div className="text-green-400">☁️ {result.layout.uploaded} enviado(s)</div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-red-400 leading-snug">
                  {result.layout.error || 'Erro desconhecido'}
                </div>
              )}
            </div>
          )}

          {/* Nota para sucesso parcial */}
          {isPartialSuccess && (
            <div className="p-2 bg-yellow-400/10 border border-yellow-400/30 rounded">
              <div className="text-[11px] text-yellow-400 leading-snug">
                ℹ️ Produção exportada com sucesso. Você pode tentar exportar apenas o layout posteriormente.
              </div>
            </div>
          )}

          {/* Mensagem adicional */}
          {result.message && (
            <div className="text-xs text-gray-400 leading-snug">
              {result.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-navy-700 flex gap-2">
          <button
            onClick={onClose}
            className={`w-full px-3 py-2 text-sm rounded font-medium transition-colors ${
              isSuccess
                ? 'bg-green-400 hover:bg-green-500 text-navy-900'
                : isPartialSuccess
                ? 'bg-yellow-400 hover:bg-yellow-500 text-navy-900'
                : 'bg-red-400 hover:bg-red-500 text-white'
            }`}
          >
            {isSuccess || isPartialSuccess ? 'Concluir' : 'Fechar'}
          </button>
        </div>
      </div>
    </>
  );
}

